import type { LocationQuery, LocationQueryValue } from 'vue-router';
import { z } from 'zod';
import type { AcademyFilter } from '~/types/academy/filters';
import { AcademyTrainingsListingSortSlug } from '~/types/academy/trainings-listing-sort';
import { AcademyTrainingsListingTab } from '~/types/academy/trainings-listing-tab';
import {
    DEFAULT_ACADEMY_TRAININGS_LISTING_SORT_SLUG,
    DEFAULT_ACADEMY_TRAININGS_LISTING_TAB,
    TRAININGS_LISTING_URL_FILTER_CODES
} from '~/utils/academy/constants';

/**
 * Input limit of the parser against denial-of-service. The parser reads at most this number of
 * entries of a repeated key, and keeps at most this number of values for each parameter. The real
 * filter option lists have fewer than 20 entries.
 */
export const TRAININGS_LISTING_URL_MAX_VALUES_PER_PARAMETER = 50;

/** Input limit of the parser against denial-of-service. The parser ignores a longer value. */
export const TRAININGS_LISTING_URL_MAX_VALUE_LENGTH = 200;

const TAB_PARAMETER = 'status';
const SORT_PARAMETER = 'sort';

/** The listing parameters in the order in which the serializer writes them. */
export const TRAININGS_LISTING_URL_PARAMETERS: readonly string[] = [
    ...TRAININGS_LISTING_URL_FILTER_CODES,
    TAB_PARAMETER,
    SORT_PARAMETER
];

/** Selected option keys for each filter code. */
export type TrainingsListingFilterSelections = Readonly<Record<string, readonly string[]>>;

export interface AcademyTrainingsListingUrlState {
    /** Holds only URL filter codes with at least one value. */
    readonly filters: TrainingsListingFilterSelections;
    readonly tab: AcademyTrainingsListingTab;
    readonly sort: AcademyTrainingsListingSortSlug;
}

// Case-insensitive and without surrounding spaces, so `?status= Open` is the same as `?status=open`.
const TabSchema = z.string().trim().toLowerCase().pipe(z.enum(AcademyTrainingsListingTab));
const SortSlugSchema = z.string().trim().toLowerCase().pipe(z.enum(AcademyTrainingsListingSortSlug));

type QueryValue = LocationQueryValue | LocationQueryValue[];

const isQueryValue = (value: unknown): value is QueryValue =>
    value === null ||
    typeof value === 'string' ||
    (Array.isArray(value) && value.every((entry) => entry === null || typeof entry === 'string'));

/**
 * Vue Router makes a query with `key in query`. A key such as `constructor` then finds an inherited
 * value, and the key `__proto__` replaces the prototype of the query. Because of this, the functions
 * read only the own properties of a query.
 */
const getOwnValue = (query: LocationQuery, key: string): QueryValue | undefined =>
    Object.hasOwn(query, key) ? query[key] : undefined;

const normalizeValue = (value: string): string => value.trim().toLowerCase();

/**
 * Splits the value or the repeated values of one parameter at commas. Removes empty values and
 * duplicates (case-insensitive, the first spelling stays) and applies the input limits.
 */
function parseParameterValues(rawValue: QueryValue | undefined): string[] {
    const entries = Array.isArray(rawValue) ? rawValue : [rawValue];
    const values: string[] = [];
    const seenValues = new Set<string>();

    for (const entry of entries.slice(0, TRAININGS_LISTING_URL_MAX_VALUES_PER_PARAMETER)) {
        if (typeof entry !== 'string') {
            continue;
        }

        // The limit of `split` stops the work early for a very long parameter.
        for (const part of entry.split(',', TRAININGS_LISTING_URL_MAX_VALUES_PER_PARAMETER)) {
            const value = part.trim();
            const normalizedValue = value.toLowerCase();

            if (!value || value.length > TRAININGS_LISTING_URL_MAX_VALUE_LENGTH || seenValues.has(normalizedValue)) {
                continue;
            }

            seenValues.add(normalizedValue);
            values.push(value);

            if (values.length === TRAININGS_LISTING_URL_MAX_VALUES_PER_PARAMETER) {
                return values;
            }
        }
    }

    return values;
}

/** With a repeated key, the first entry decides. */
function getFirstValue(rawValue: QueryValue | undefined): LocationQueryValue | undefined {
    return Array.isArray(rawValue) ? rawValue[0] : rawValue;
}

/** Reads only the listing parameters of a route query. Invalid `status` and `sort` values give the defaults. */
export function parseTrainingsListingUrlState(query: LocationQuery): AcademyTrainingsListingUrlState {
    const filters: Record<string, string[]> = {};

    for (const code of TRAININGS_LISTING_URL_FILTER_CODES) {
        const values = parseParameterValues(getOwnValue(query, code));

        if (values.length) {
            filters[code] = values;
        }
    }

    const tab = TabSchema.safeParse(getFirstValue(getOwnValue(query, TAB_PARAMETER)));
    const sort = SortSlugSchema.safeParse(getFirstValue(getOwnValue(query, SORT_PARAMETER)));

    return {
        filters,
        tab: tab.success ? tab.data : DEFAULT_ACADEMY_TRAININGS_LISTING_TAB,
        sort: sort.success ? sort.data : DEFAULT_ACADEMY_TRAININGS_LISTING_SORT_SLUG
    };
}

/**
 * Keeps only the values that are an option key of the filter with the same code. The match is
 * case-insensitive and ignores surrounding spaces. Each kept value is the option key itself, in
 * the order of the options, so the same selection always gives the same URL.
 */
export function validateTrainingsListingFilters(
    filters: TrainingsListingFilterSelections,
    filterOptions: readonly AcademyFilter[]
): Record<string, string[]> {
    const validatedFilters: Record<string, string[]> = {};

    for (const [code, values] of Object.entries(filters)) {
        const options = filterOptions.find((filter) => filter.code === code)?.params.options ?? [];
        // A requested value matches the first option only, so it cannot select two options.
        const unmatchedValues = new Set(values.map(normalizeValue));
        const validKeys: string[] = [];

        for (const option of options) {
            if (unmatchedValues.delete(normalizeValue(option.key))) {
                validKeys.push(option.key);
            }
        }

        if (validKeys.length) {
            validatedFilters[code] = validKeys;
        }
    }

    return validatedFilters;
}

/** Turns a state into listing parameters. Omits the defaults and the empty filters. */
export function serializeTrainingsListingUrlState(state: AcademyTrainingsListingUrlState): Record<string, string> {
    const parameters: Record<string, string> = {};

    for (const code of TRAININGS_LISTING_URL_FILTER_CODES) {
        const values = state.filters[code];

        if (values?.length) {
            parameters[code] = values.join(',');
        }
    }

    if (state.tab !== DEFAULT_ACADEMY_TRAININGS_LISTING_TAB) {
        parameters[TAB_PARAMETER] = state.tab;
    }

    if (state.sort !== DEFAULT_ACADEMY_TRAININGS_LISTING_SORT_SLUG) {
        parameters[SORT_PARAMETER] = state.sort;
    }

    return parameters;
}

export function pickTrainingsListingUrlParameters(query: LocationQuery): LocationQuery {
    const listingParameters: LocationQuery = {};

    for (const parameter of TRAININGS_LISTING_URL_PARAMETERS) {
        const value = getOwnValue(query, parameter);

        if (value !== undefined) {
            listingParameters[parameter] = value;
        }
    }

    return listingParameters;
}

/**
 * Replaces the listing parameters of a query. All other parameters keep their values and their
 * order, and the listing parameters follow them. A value that is not a string, `null` or an array of
 * these (for example a function that a key such as `constructor` gives) does not stay.
 */
export function mergeTrainingsListingUrlQuery(
    currentQuery: LocationQuery,
    listingParameters: Record<string, string>
): LocationQuery {
    const otherParameters = Object.entries(currentQuery).filter(
        ([key, value]) => !TRAININGS_LISTING_URL_PARAMETERS.includes(key) && isQueryValue(value)
    );

    return { ...Object.fromEntries(otherParameters), ...listingParameters };
}

// Same rule as Vue Router: an array with one entry is equal to its entry, because both give the same URL.
function isSameQueryValue(first: QueryValue, second: QueryValue): boolean {
    const firstValues = Array.isArray(first) ? first : [first];
    const secondValues = Array.isArray(second) ? second : [second];

    return (
        firstValues.length === secondValues.length && firstValues.every((value, index) => value === secondValues[index])
    );
}

type RouteQueryWithUndefined = Readonly<Record<string, QueryValue | undefined>>;

const getDefinedEntries = (query: RouteQueryWithUndefined): Map<string, QueryValue> =>
    new Map(Object.entries(query).filter((entry): entry is [string, QueryValue] => entry[1] !== undefined));

/** Compares two queries without regard to the key order. A key with the value `undefined` is the same as no key. */
export function isSameRouteQuery(first: RouteQueryWithUndefined, second: RouteQueryWithUndefined): boolean {
    const firstEntries = getDefinedEntries(first);
    const secondEntries = getDefinedEntries(second);

    return (
        firstEntries.size === secondEntries.size &&
        [...firstEntries].every(([key, value]) => {
            const secondValue = secondEntries.get(key);

            return secondValue !== undefined && isSameQueryValue(value, secondValue);
        })
    );
}
